import useScrollAnimation from "@/hooks/useScrollAnimation";
import { caseStudiesData } from "@/mocks/homeData";

export default function CaseStudiesSection() {
  const { ref, isVisible } = useScrollAnimation(0.06);

  const handleNavClick = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="case-studies" className="w-full py-16 md:py-24 bg-background-50">
      <div
        ref={ref}
        className={`w-full px-4 md:px-6 lg:px-10 transition-all duration-700 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
          <span className="text-xs md:text-sm font-semibold text-primary-500 uppercase tracking-widest">
            {caseStudiesData.label}
          </span>
          <h2 className="font-heading font-bold text-xl md:text-3xl lg:text-4xl text-foreground-950 mt-3 mb-4 leading-tight">
            {caseStudiesData.headline}
          </h2>
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed max-w-2xl mx-auto">
            {caseStudiesData.subheadline}
          </p>
        </div>

        <div className="space-y-5 md:space-y-6 max-w-5xl mx-auto">
          {caseStudiesData.cases.map((cs) => (
            <div
              key={cs.title}
              className="bg-background-100 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all"
            >
              <div className="p-5 md:p-7">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-5">
                  <div className="flex-1">
                    <span className="inline-block text-xs font-semibold text-primary-600 bg-primary-50 px-2.5 py-0.5 rounded-full mb-3">
                      {cs.industry}
                    </span>
                    <h3 className="font-heading font-semibold text-base md:text-lg text-foreground-950 mb-3">
                      {cs.title}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-5">
                      <div>
                        <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                          The Challenge
                        </span>
                        <p className="text-sm text-foreground-700 leading-relaxed mt-1">
                          {cs.challenge}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                          Our Approach
                        </span>
                        <p className="text-sm text-foreground-700 leading-relaxed mt-1">
                          {cs.solution}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-background-200/70 pt-4 mb-5">
                  <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                    Results
                  </span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
                    {cs.results.map((r) => (
                      <div key={r} className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-primary-500 text-sm mt-0.5 shrink-0" />
                        <span className="text-sm text-foreground-800 leading-snug">{r}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-background-200/50 rounded-lg p-4">
                  <i className="ri-double-quotes-l text-accent-500 text-lg shrink-0" />
                  <div>
                    <p className="text-sm text-foreground-700 italic leading-relaxed mb-2">
                      &ldquo;{cs.testimonial.quote}&rdquo;
                    </p>
                    <p className="text-xs font-semibold text-foreground-900">
                      &mdash; {cs.testimonial.author}{cs.testimonial.company ? `, ${cs.testimonial.company}` : ""}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <button
            onClick={() => handleNavClick("#contact")}
            className="px-6 py-3 text-sm font-semibold rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Discuss Your Project
          </button>
        </div>
      </div>
    </section>
  );
}